
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Map, Tag, Users, Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const DashboardOverview = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    blogPosts: 0,
    itineraries: 0,
    tags: 0,
    users: 0,
    recentBlogPosts: 0,
    recentItineraries: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);
        
        // Fetch blog posts count
        const { count: blogCount, error: blogError } = await supabase
          .from('blog_posts')
          .select('*', { count: 'exact', head: true });
          
        // Fetch recent blog posts (last 7 days)
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const { count: recentBlogCount, error: recentBlogError } = await supabase
          .from('blog_posts')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', weekAgo.toISOString());
          
        // Fetch itineraries count
        const { count: itinerariesCount, error: itinerariesError } = await supabase
          .from('itineraries')
          .select('*', { count: 'exact', head: true });
          
        // Fetch recent itineraries (last 7 days)
        const { count: recentItinerariesCount, error: recentItinerariesError } = await supabase
          .from('itineraries')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', weekAgo.toISOString());
          
        // Fetch tags count
        const { count: tagsCount, error: tagsError } = await supabase
          .from('tags')
          .select('*', { count: 'exact', head: true });
          
        // Fetch users count (only if admin)
        let usersCount = 0;
        if (isAdmin()) {
          const { count: profilesCount, error: profilesError } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true });
            
          if (!profilesError) {
            usersCount = profilesCount || 0;
          }
        }
          
        setCounts({
          blogPosts: blogCount || 0,
          itineraries: itinerariesCount || 0,
          tags: tagsCount || 0,
          users: usersCount,
          recentBlogPosts: recentBlogCount || 0,
          recentItineraries: recentItinerariesCount || 0
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCounts();
  }, [isAdmin]);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
      <p className="text-muted-foreground">Welcome to your travel CMS dashboard.</p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card 
          className="glass-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/dashboard/blogs')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Blog Posts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-10 flex items-center">
                <div className="w-8 h-5 bg-gray-200 animate-pulse rounded"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{counts.blogPosts}</div>
                <p className="text-xs text-muted-foreground">
                  {counts.recentBlogPosts} published this week
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card 
          className="glass-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/dashboard/itineraries')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Itineraries</CardTitle>
            <Map className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-10 flex items-center">
                <div className="w-8 h-5 bg-gray-200 animate-pulse rounded"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{counts.itineraries}</div>
                <p className="text-xs text-muted-foreground">
                  {counts.recentItineraries} created this week
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card 
          className="glass-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/dashboard/tags')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tags</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-10 flex items-center">
                <div className="w-8 h-5 bg-gray-200 animate-pulse rounded"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{counts.tags}</div>
                <p className="text-xs text-muted-foreground">Available for categorization</p>
              </>
            )}
          </CardContent>
        </Card>
        
        {isAdmin() && (
          <Card 
            className="glass-card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/dashboard/users')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-10 flex items-center">
                  <div className="w-8 h-5 bg-gray-200 animate-pulse rounded"></div>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-bold">{counts.users}</div>
                  <p className="text-xs text-muted-foreground">Active system users</p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <h2 className="text-xl font-semibold mt-8 mb-4">Recent Activity</h2>
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Recent actions taken by users</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="rounded-full bg-gray-200 animate-pulse h-10 w-10"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 animate-pulse rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : counts.blogPosts === 0 && counts.itineraries === 0 ? (
            <div className="py-4 text-center text-muted-foreground">
              No activity yet. Start by creating blog posts or itineraries.
            </div>
          ) : (
            <div className="space-y-4">
              {counts.recentBlogPosts > 0 && (
                <ActivityItem
                  icon={<FileText className="h-4 w-4" />}
                  title="New blog posts created"
                  description={`${counts.recentBlogPosts} blog posts were created recently`}
                  timestamp="This week"
                />
              )}
              {counts.recentItineraries > 0 && (
                <ActivityItem
                  icon={<Map className="h-4 w-4" />}
                  title="New itineraries created"
                  description={`${counts.recentItineraries} itineraries were created recently`}
                  timestamp="This week"
                />
              )}
              {counts.tags > 0 && (
                <ActivityItem
                  icon={<Tag className="h-4 w-4" />}
                  title="Tags available"
                  description={`${counts.tags} tags available for categorization`}
                  timestamp="Total"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface ActivityItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  timestamp: string;
}

const ActivityItem = ({ icon, title, description, timestamp }: ActivityItemProps) => (
  <div className="flex items-start space-x-4">
    <div className="rounded-full bg-primary/10 p-2">
      {icon}
    </div>
    <div className="flex-1 space-y-1">
      <p className="text-sm font-medium leading-none">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
    <div className="text-xs text-muted-foreground">{timestamp}</div>
  </div>
);

export default DashboardOverview;
